import { useRef, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { ASSETS } from "../data/content";
import { Reveal } from "./Reveal";

const BeforeAfter = () => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(clientX);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  return (
    <section
      id="proof"
      data-testid="before-after-section"
      className="relative bg-white py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#58cc02] text-center">
            Real copies · Real evaluation
          </p>
          <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tight text-center mt-4 text-[#111827]">
            From <span className="text-[#ef4444]">0.5</span> to{" "}
            <span className="text-[#58cc02]">8</span> marks.
          </h2>
          <p className="text-center text-lg text-[#4b5563] max-w-2xl mx-auto mt-5">
            This is a genuine student from our FREE Answer Writing Club — same
            hand, two months apart. Drag the handle to watch the transformation.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            ref={containerRef}
            data-testid="before-after-slider"
            className="relative mt-14 rounded-3xl overflow-hidden select-none shadow-[0_30px_60px_rgb(0,0,0,0.12)] border-4 border-white bg-[#f8f9fa] cursor-ew-resize"
            style={{ aspectRatio: "3 / 4", maxHeight: "80vh", margin: "0 auto" }}
            onMouseDown={(e) => {
              dragging.current = true;
              setFromClientX(e.clientX);
            }}
            onTouchStart={(e) => {
              dragging.current = true;
              setFromClientX(e.touches[0].clientX);
            }}
          >
            {/* AFTER (base) */}
            <img
              src={ASSETS.after}
              alt="8 marks answer copy — after"
              className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
              draggable={false}
            />
            <div className="absolute top-4 right-4 bg-[#58cc02] text-white font-extrabold text-sm px-4 py-2 rounded-full shadow-lg z-10">
              AFTER · 8 / 15
            </div>

            {/* BEFORE (clipped) */}
            <div
              className="absolute inset-0 overflow-hidden pointer-events-none"
              style={{ width: `${pos}%` }}
            >
              <img
                src={ASSETS.before}
                alt="0.5 marks answer copy — before"
                className="absolute inset-0 h-full object-cover object-top"
                style={{ width: `${(100 / pos) * 100}%`, maxWidth: "none" }}
                draggable={false}
              />
              <div className="absolute top-4 left-4 bg-[#ef4444] text-white font-extrabold text-sm px-4 py-2 rounded-full shadow-lg">
                BEFORE · 0.5
              </div>
            </div>

            {/* Handle */}
            <div
              className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.1)] z-20"
              style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-white shadow-[0_8px_20px_rgb(0,0,0,0.2)] flex items-center justify-center border-2 border-[#58cc02]">
                <MoveHorizontal className="h-6 w-6 text-[#58cc02]" />
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { k: "Before", v: "Wasted a full page on intro, vague framing.", c: "#ef4444" },
              { k: "The fix", v: "Tighter structure, flowchart thinking, examples.", c: "#111827" },
              { k: "After", v: "Clean flow, 'good' remarks, 8/15 and climbing.", c: "#58cc02" },
            ].map((item) => (
              <div
                key={item.k}
                className="rounded-2xl bg-[#f8f9fa] border border-black/5 p-6"
              >
                <span
                  className="text-xs font-extrabold uppercase tracking-[0.2em]"
                  style={{ color: item.c }}
                >
                  {item.k}
                </span>
                <p className="mt-2 text-[#4b5563] font-semibold leading-relaxed">
                  {item.v}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default BeforeAfter;
